import React, { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { Button } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faSearch,
  faDownload,
  faRotateRight,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-hot-toast";

interface CompanyRow {
  modelName: string;
  serial: string;
  normalizedSerial: string;
  sourceRow: number;
}

interface AgentRow {
  agentModelName: string;
  serial: string;
  productId: string;
  point: string;
  normalizedSerial: string;
  sourceRow: number;
  originalRow: any;
}

interface ResultRow {
  serial: string;
  normalizedSerial: string;
  agentModelName: string;
  correctModelName: string;
  productId: string;
  point: string;
  agentSourceRow: number;
  companySourceRow?: number;
  status: "matched" | "not_found";
}

const SerialModelNameMapper: React.FC = () => {
  const [companyFileName, setCompanyFileName] = useState("");
  const [agentFileName, setAgentFileName] = useState("");

  const [companyRows, setCompanyRows] = useState<CompanyRow[]>([]);
  const [agentRows, setAgentRows] = useState<AgentRow[]>([]);

  const [searchQuery, setSearchQuery] = useState("");

  const normalizeDigits = (value: string) => {
    const arabicDigits = "٠١٢٣٤٥٦٧٨٩";
    const persianDigits = "۰۱۲۳۴۵۶۷۸۹";

    return value.replace(/[٠-٩۰-۹]/g, (digit) => {
      const arabicIndex = arabicDigits.indexOf(digit);
      if (arabicIndex > -1) return String(arabicIndex);

      const persianIndex = persianDigits.indexOf(digit);
      if (persianIndex > -1) return String(persianIndex);

      return digit;
    });
  };

  const normalizeSerial = (value: any) => {
    return normalizeDigits(String(value ?? ""))
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .replace(/\s+/g, "")
      .replace(/[-–—]/g, "-")
      .trim()
      .toUpperCase();
  };

  const normalizeText = (value: any) => {
    return String(value ?? "")
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .replace(/\s+/g, " ")
      .trim();
  };

  const normalizeHeader = (value: string) => {
    return String(value ?? "")
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .replace(/\s+/g, "")
      .replace(/_/g, "")
      .replace("-", "")
      .trim()
      .toLowerCase();
  };

  const findColumnKey = (row: any, aliases: string[]) => {
    const keys = Object.keys(row);
    const normalizedAliases = aliases.map(normalizeHeader);

    return keys.find((key) => {
      const normalizedKey = normalizeHeader(key);
      return normalizedAliases.includes(normalizedKey);
    });
  };

  const getValue = (row: any, aliases: string[]) => {
    const key = findColumnKey(row, aliases);
    return key ? row[key] : "";
  };

  const readExcelRows = async (file: File) => {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data);

    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];

    return XLSX.utils.sheet_to_json<any>(worksheet, {
      defval: "",
    });
  };

  const parseCompanyFile = async (file: File) => {
    const jsonData = await readExcelRows(file);
    const parsedRows: CompanyRow[] = [];

    jsonData.forEach((row, index) => {
      const serial = normalizeText(
        getValue(row, [
          "الرقم التسلسلي",
          "السيريال",
          "سيريال",
          "Serial",
          "serial",
          "Serial Number",
          "serialNumber",
          "SN",
          "S/N",
        ])
      );

      const modelName = normalizeText(
        getValue(row, [
          "اسم الموديل",
          "الموديل",
          "اسم المادة",
          "Model",
          "Model Name",
          "modelName",
          "Item Name",
          "itemName",
          "Product Name",
          "productName",
        ])
      );

      const normalizedSerial = normalizeSerial(serial);

      if (!normalizedSerial) return;

      parsedRows.push({
        modelName,
        serial,
        normalizedSerial,
        sourceRow: index + 2,
      });
    });

    return parsedRows;
  };

  const parseAgentFile = async (file: File) => {
    const jsonData = await readExcelRows(file);
    const parsedRows: AgentRow[] = [];

    jsonData.forEach((row, index) => {
      const serial = normalizeText(
        getValue(row, [
          "الرقم التسلسلي",
          "السيريال",
          "سيريال",
          "Serial",
          "serial",
          "Serial Number",
          "serialNumber",
          "SN",
          "S/N",
        ])
      );

      const agentModelName = normalizeText(
        getValue(row, [
          "اسم الموديل",
          "الموديل",
          "اسم المادة",
          "Model",
          "Model Name",
          "modelName",
          "Item Name",
          "itemName",
          "Product Name",
          "productName",
        ])
      );

      const productId = normalizeText(
        getValue(row, [
          "product id",
          "productId",
          "Product ID",
          "ProductId",
          "رقم المنتج",
          "معرف المنتج",
        ])
      );

      const point = normalizeText(
        getValue(row, [
          "point",
          "Point",
          "points",
          "Points",
          "النقاط",
          "نقاط",
        ])
      );

      const normalizedSerial = normalizeSerial(serial);

      if (!normalizedSerial) return;

      parsedRows.push({
        agentModelName,
        serial,
        productId,
        point,
        normalizedSerial,
        sourceRow: index + 2,
        originalRow: row,
      });
    });

    return parsedRows;
  };

  const handleCompanyUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const parsedRows = await parseCompanyFile(file);

      setCompanyRows(parsedRows);
      setCompanyFileName(file.name);

      toast.success(`تم رفع ملف الشركة: ${parsedRows.length} سيريال`);
      event.target.value = "";
    } catch (error) {
      console.error("Company Excel parse error:", error);
      toast.error("حدث خطأ أثناء قراءة ملف الشركة");
    }
  };

  const handleAgentUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const parsedRows = await parseAgentFile(file);

      setAgentRows(parsedRows);
      setAgentFileName(file.name);

      toast.success(`تم رفع ملف الوكيل: ${parsedRows.length} سيريال`);
      event.target.value = "";
    } catch (error) {
      console.error("Agent Excel parse error:", error);
      toast.error("حدث خطأ أثناء قراءة ملف الوكيل");
    }
  };

  const results = useMemo<ResultRow[]>(() => {
    const companyMap = new Map<string, CompanyRow>();

    companyRows.forEach((row) => {
      if (!companyMap.has(row.normalizedSerial)) {
        companyMap.set(row.normalizedSerial, row);
      }
    });

    return agentRows.map((agentRow) => {
      const companyRow = companyMap.get(agentRow.normalizedSerial);

      return {
        serial: agentRow.serial,
        normalizedSerial: agentRow.normalizedSerial,
        agentModelName: agentRow.agentModelName,
        correctModelName: companyRow?.modelName || "",
        productId: agentRow.productId,
        point: agentRow.point,
        agentSourceRow: agentRow.sourceRow,
        companySourceRow: companyRow?.sourceRow,
        status: companyRow ? "matched" : "not_found",
      };
    });
  }, [companyRows, agentRows]);

  const matchedCount = results.filter((x) => x.status === "matched").length;
  const notFoundCount = results.filter((x) => x.status === "not_found").length;

  const duplicatedCompanySerialsCount = useMemo(() => {
    const counts = new Map<string, number>();

    companyRows.forEach((row) => {
      counts.set(row.normalizedSerial, (counts.get(row.normalizedSerial) || 0) + 1);
    });

    return Array.from(counts.values()).filter((count) => count > 1).length;
  }, [companyRows]);

  const visibleRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    if (!q) return results;

    return results.filter((row) => {
      return (
        row.serial.toLowerCase().includes(q) ||
        row.normalizedSerial.toLowerCase().includes(q) ||
        row.agentModelName.toLowerCase().includes(q) ||
        row.correctModelName.toLowerCase().includes(q) ||
        row.productId.toLowerCase().includes(q) ||
        row.point.toLowerCase().includes(q) ||
        row.status.toLowerCase().includes(q)
      );
    });
  }, [results, searchQuery]);

  const buildExportData = () => {
    return results.map((row, index) => ({
      "#": index + 1,
      "السيريال": row.serial,
      "Normalized Serial": row.normalizedSerial,
      "اسم الموديل في ملف الوكيل": row.agentModelName,
      "اسم الموديل الصحيح من ملف الشركة": row.correctModelName || "غير موجود في ملف الشركة",
      "Product ID": row.productId,
      Point: row.point,
      "صف الوكيل": row.agentSourceRow,
      "صف الشركة": row.companySourceRow || "",
      "الحالة": row.status === "matched" ? "مطابق" : "غير موجود في ملف الشركة",
    }));
  };

  const exportResults = () => {
    if (results.length === 0) {
      toast.error("يرجى رفع ملف الشركة وملف الوكيل أولاً");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(buildExportData());
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Corrected Models");

    XLSX.writeFile(workbook, "agent_serials_with_correct_model_names.xlsx");
  };

  const handleReset = () => {
    setCompanyFileName("");
    setAgentFileName("");
    setCompanyRows([]);
    setAgentRows([]);
    setSearchQuery("");
  };

  return (
    <div
      className="w-full max-w-none mx-auto bg-white rounded-xl shadow-lg"
      style={{ width: 1400, direction: "rtl" }}
    >
      <div className="max-w-full flex items-center justify-between bg-[#262d34] text-white px-6 py-3 rounded-t-lg shadow-md">
        <h1
          className="text-xl font-bold flex-1 text-right"
          style={{ fontFamily: "Tajawal, sans-serif" }}
        >
          تصحيح أسماء الموديلات حسب السيريالات
        </h1>

        <div className="flex items-center gap-3">
          <label>
            <input
              type="file"
              accept=".xlsx,.xls"
              hidden
              onChange={handleCompanyUpload}
            />

            <Button
              variant="contained"
              component="span"
              sx={{
                fontFamily: "Tajawal, sans-serif",
                backgroundColor: "#2563eb",
              }}
              startIcon={<FontAwesomeIcon icon={faUpload} />}
            >
              رفع ملف الشركة
            </Button>
          </label>

          <label>
            <input
              type="file"
              accept=".xlsx,.xls"
              hidden
              onChange={handleAgentUpload}
            />

            <Button
              variant="contained"
              component="span"
              sx={{
                fontFamily: "Tajawal, sans-serif",
                backgroundColor: "#0f766e",
              }}
              startIcon={<FontAwesomeIcon icon={faUpload} />}
            >
              رفع ملف الوكيل
            </Button>
          </label>

          <Button
            variant="outlined"
            onClick={handleReset}
            sx={{
              fontFamily: "Tajawal, sans-serif",
              color: "#fff",
              borderColor: "#fff",
            }}
            startIcon={<FontAwesomeIcon icon={faRotateRight} />}
          >
            إعادة تعيين
          </Button>
        </div>
      </div>

      <div
        style={{
          padding: "16px 24px",
          display: "grid",
          gridTemplateColumns: "repeat(5, minmax(180px, 1fr))",
          gap: 14,
          fontFamily: "Tajawal, sans-serif",
        }}
      >
        <StatCard
          title="سيريالات الشركة"
          value={companyRows.length}
          color="#2563eb"
          subTitle={companyFileName || "لم يتم رفع ملف الشركة"}
        />

        <StatCard
          title="سيريالات الوكيل"
          value={agentRows.length}
          color="#0f766e"
          subTitle={agentFileName || "لم يتم رفع ملف الوكيل"}
        />

        <StatCard
          title="تم تصحيحها"
          value={matchedCount}
          color="#16a34a"
          subTitle="وجد لها اسم صحيح"
        />

        <StatCard
          title="غير موجودة بالشركة"
          value={notFoundCount}
          color="#dc2626"
          subTitle="تحتاج مراجعة"
        />

        <StatCard
          title="تكرار في ملف الشركة"
          value={duplicatedCompanySerialsCount}
          color="#ea580c"
          subTitle="نفس السيريال مكرر"
        />
      </div>

      <div
        style={{
          padding: "0 24px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          fontFamily: "Tajawal, sans-serif",
        }}
      >
        <div className="flex items-center bg-gray-100 rounded-full px-4 py-1 w-96 shadow-md">
          <FontAwesomeIcon icon={faSearch} className="text-gray-500 ml-2" />
          <input
            type="text"
            placeholder="البحث بالسيريال، اسم الموديل، Product ID، Point..."
            className="bg-transparent focus:outline-none w-full text-sm text-gray-700 py-1"
            style={{
              fontFamily: "Tajawal, sans-serif",
              textAlign: "right",
            }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <Button
          variant="contained"
          color="success"
          onClick={exportResults}
          disabled={results.length === 0}
          sx={{ fontFamily: "Tajawal, sans-serif" }}
          startIcon={<FontAwesomeIcon icon={faDownload} />}
        >
          تنزيل ملف الوكيل بالأسماء الصحيحة
        </Button>
      </div>

      {results.length === 0 ? (
        <div
          style={{
            padding: 30,
            textAlign: "center",
            fontFamily: "Tajawal, sans-serif",
            color: "#64748b",
            fontSize: 16,
          }}
        >
          يرجى رفع ملف الشركة وملف الوكيل. يجب أن يحتوي كل ملف على عمود السيريال وعمود اسم الموديل.
        </div>
      ) : (
        <div className="table-container" style={{ overflowX: "auto" }}>
          <table className="product-table" style={{ width: "100%" }}>
            <thead className="color-table">
              <tr>
                <th>#</th>
                <th>السيريال</th>
                <th>اسم الموديل في ملف الوكيل</th>
                <th>اسم الموديل الصحيح من الشركة</th>
                <th>Product ID</th>
                <th>Point</th>
                <th>صف الوكيل</th>
                <th>صف الشركة</th>
                <th>الحالة</th>
              </tr>
            </thead>

            <tbody>
              {visibleRows.map((row, index) => (
                <tr key={`${row.normalizedSerial}-${row.agentSourceRow}`}>
                  <td>{index + 1}</td>

                  <td style={{ direction: "ltr" }}>{row.serial || "-"}</td>

                  <td>{row.agentModelName || "-"}</td>

                  <td>
                    {row.correctModelName ? (
                      <strong style={{ color: "#166534" }}>
                        {row.correctModelName}
                      </strong>
                    ) : (
                      <span style={{ color: "#b91c1c" }}>
                        غير موجود في ملف الشركة
                      </span>
                    )}
                  </td>

                  <td>{row.productId || "-"}</td>
                  <td>{row.point || "-"}</td>
                  <td>{row.agentSourceRow}</td>
                  <td>{row.companySourceRow || "-"}</td>

                  <td>
                    {row.status === "matched" ? (
                      <span
                        style={{
                          background: "#dcfce7",
                          color: "#166534",
                          padding: "5px 10px",
                          borderRadius: 999,
                          fontSize: 13,
                          fontWeight: "bold",
                        }}
                      >
                        مطابق
                      </span>
                    ) : (
                      <span
                        style={{
                          background: "#fee2e2",
                          color: "#b91c1c",
                          padding: "5px 10px",
                          borderRadius: 999,
                          fontSize: 13,
                          fontWeight: "bold",
                        }}
                      >
                        غير موجود
                      </span>
                    )}
                  </td>
                </tr>
              ))}

              {visibleRows.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ textAlign: "center", padding: 20 }}>
                    لا توجد بيانات
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const StatCard: React.FC<{
  title: string;
  value: number;
  color: string;
  subTitle?: string;
}> = ({ title, value, color, subTitle }) => {
  return (
    <div
      style={{
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        padding: "14px 18px",
        textAlign: "center",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        minHeight: 105,
      }}
    >
      <div style={{ fontSize: 14, color: "#64748b", marginBottom: 6 }}>
        {title}
      </div>

      <div style={{ fontSize: 28, fontWeight: "bold", color }}>{value}</div>

      {subTitle && (
        <div
          style={{
            fontSize: 12,
            color: "#64748b",
            marginTop: 5,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={subTitle}
        >
          {subTitle}
        </div>
      )}
    </div>
  );
};

export default SerialModelNameMapper;